import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo, 
} from "react";

import YetiNotifications from "../services/YetiNotifications";

const LOCAL_STORAGE_KEY = "le_notifications";
const FEED_SIZE = 5;
const FROM_DATE = 6; // in months

const LightEdgeNotificationsContext = createContext();

export const LightEdgeNotificationsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [latestNotifications, setLatestNotifications] = useState([]);
  const [latestFeedNotifications, setLatesFeedNotifications] = useState([]);

  const getDismissedNotifications = useCallback(() => {
    const dismissed = localStorage.getItem(LOCAL_STORAGE_KEY);
    return dismissed ? JSON.parse(dismissed) : [];
  }, []);

  const setDismissedNotifications = useCallback((ids) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(ids));
  }, []);

  const dismissNotification = useCallback(
    (id) => {
      const dismissed = getDismissedNotifications();
      const updated = [...new Set([...dismissed, id])];
      setDismissedNotifications(updated);
      setLatestNotifications((prev) => prev.filter((n) => n.id !== id));
    },
    [getDismissedNotifications, setDismissedNotifications]
  );

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const date = new Date();
        date.setMonth(date.getMonth() - FROM_DATE);
        const fromDate = date.toISOString().split("T")[0];
        
        const notificationResponse = await YetiNotifications.getNotificationList(fromDate);
        
        const leNotifications = Array.isArray(notificationResponse?.data?.results)
          ? [...notificationResponse.data.results]
          : [];
        
        const dismissed = getDismissedNotifications();
        const now = new Date();
        
        const parseTime = (ts) => Date.parse(ts) || 0;
        const byCreatedDesc = (a, b) => parseTime(b.created) - parseTime(a.created);
        
        // Only show notifications not dismissed
        const visibleLENotifications = leNotifications.filter(
          (n) => !dismissed.includes(n.id)
        );
        
        const leNotificationsFeed = [...leNotifications]
          .sort(byCreatedDesc)
          .slice(0, FEED_SIZE);
        
        const latestVisible = visibleLENotifications
          .filter((notification) => {
            const { type, window_end } = notification;
            const end = new Date(window_end);
            // Filter out only if type is INFO or CHANGE and window_end is in the past
            return !((type === "INFO" || type === "CHANGE") && end < now);
          })
          .sort(byCreatedDesc)
          .slice(0, FEED_SIZE);
        
        setNotifications(leNotifications);
        setLatestNotifications(latestVisible);
        setLatesFeedNotifications(leNotificationsFeed);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [getDismissedNotifications]);

  const contextValues = useMemo(
    () => ({
      loading,
      notifications,
      latestNotifications,
      latestFeedNotifications,
      dismissNotification,
    }),
    [loading, notifications, latestNotifications, dismissNotification]
  );

  return (
    <LightEdgeNotificationsContext.Provider value={contextValues}>
      {children}
    </LightEdgeNotificationsContext.Provider>
  );
};

export const useLightEdgeNotifications = () => {
  const context = useContext(LightEdgeNotificationsContext);
  if (!context) {
    throw new Error(
      "useLightEdgeNotifications must be used within a LightEdgeNotificationsProvider"
    );
  }
  return context;
};
